import React from 'react';
import configuration from './../configuration/server';
import AWS from 'aws-sdk';
import { CognitoUserPool, AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js';
import { FormattedMessage } from 'react-intl';
import mixpanel from 'mixpanel-browser';

import { Snackbar } from '../components/snackbar';
import { call } from './../utilities/http';
import {
    LOADING,
    COMPLETE,
    IDENTITY_LOGIN_OK,
    IDENTITY_LOGOUT_OK,
    IDENTITY_SET_FORCE_CHANGE_PASSWORD,
    IDENTITY_UNSET_FORCE_CHANGE_PASSWORD,
    IDENTITY_FORGOT_PASSWORD,
    IDENTITY_RESET_PASSWORD_COMPLETE,
    IDENTITY_GET_ROLES,
    IDENTITY_SET_IMPERSONATE_ROLE,
    IDENTITY_SELECT_ROLE,
    IDENTITY_UPDATE_ROLE_STATUS,
    IDENTITY_GET_USER_DETAILS,
    IDENTITY_UPDATE_USER_DETAILS,
    IDENTITY_GET_USER_PROFILE,
    IDENTITY_GET_ADMIN_OK,
    SAVING
} from './types';
import { DataStorage } from '../utilities/storage';
import { frontEndVersion, frontEndCommitHash } from '../utilities/version';

const defaultState = {
    user: undefined,
    details: undefined,
    admin: undefined,
    isAuthenticated: false,
    role: undefined,
    roles: undefined,
    profile: undefined,
    forceChangePassword: false,
    session: undefined,
    impersonatedUser: undefined
};

export const userPool = new CognitoUserPool({
    UserPoolId: configuration[configuration.STAGE].cognito.USER_POOL_ID,
    ClientId: configuration[configuration.STAGE].cognito.APP_CLIENT_ID
});

/**
 * Identity state mapper.
 * @param {object} state
 * @param {string} action
 */
export default function(state = defaultState, action) {
    switch (action.type) {
        // User authentication
        case IDENTITY_LOGIN_OK:
            if (!action || !action.data) {
                return defaultState;
            }

            let tokenPayload = action.data.getIdToken();
            if (!tokenPayload) {
                return defaultState;
            }

            let token = tokenPayload.decodePayload();
            if (!token) {
                return defaultState;
            }

            const user = token;
            mixpanel.identify(user.sub);
            mixpanel.register({ feVersion: frontEndVersion(), feHash: frontEndCommitHash() });
            mixpanel.track(IDENTITY_LOGIN_OK);

            return { ...state, user, isAuthenticated: true };

        case IDENTITY_LOGOUT_OK:
            return defaultState;

        // Mandatory reset password
        case IDENTITY_SET_FORCE_CHANGE_PASSWORD:
            return { ...state, forceChangePassword: true, session: action.data };

        case IDENTITY_UNSET_FORCE_CHANGE_PASSWORD:
            return { ...state, forceChangePassword: false, session: undefined };

        // Forgot password flow
        case IDENTITY_FORGOT_PASSWORD:
            mixpanel.track(IDENTITY_FORGOT_PASSWORD);
            return { ...state, session: action.data };

        case IDENTITY_RESET_PASSWORD_COMPLETE:
            return { ...state, session: undefined };

        // Profile data
        case IDENTITY_GET_ROLES:
            return { ...state, roles: action.data };

        case IDENTITY_SET_IMPERSONATE_ROLE:
            return { ...state, impersonatedUser: action.data };

        case IDENTITY_GET_USER_PROFILE:
            if (action && action.data) {
                const { email, first_name, last_name } = action.data;
                mixpanel.people.set({ first_name, last_name, email });
            }
            return { ...state, profile: state.profile ? { ...state.profile, ...action.data } : action.data };

        case IDENTITY_UPDATE_ROLE_STATUS:
            let roles = state.roles.filter(r => r.role !== action.data.role && r.type !== action.data.type && r.id !== action.data.id);
            return { ...state, roles: [...roles, action.data] };

        case IDENTITY_GET_USER_DETAILS:
            return { ...state, details: action.data };

        case IDENTITY_UPDATE_USER_DETAILS:
            return { ...state, details: { ...state.details, ...action.data } };

        case IDENTITY_GET_ADMIN_OK:
            return { ...state, admin: action.data };

        default:
            return state;
    }
}

/**
 * Attempt to authenticate user with provided email and password.
 * @param {object} values
 */
export function login(values, callbacks = {}, skipForcingPasswordChange = false) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        let email = values.email.toLowerCase();
        const user = new CognitoUser({ Username: email, Pool: userPool });
        const authenticationData = { Username: email, Password: values.password };
        const authenticationDetails = new AuthenticationDetails(authenticationData);

        user.authenticateUser(authenticationDetails, {
            onSuccess: session => {
                dispatch({ type: IDENTITY_LOGIN_OK, data: session });
                if (callbacks.onSuccess) {
                    callbacks.onSuccess(session);
                }
                dispatch({ type: COMPLETE });
            },
            onFailure: error => {
                Snackbar.error(error.message, { autoClose: false });
                if (callbacks.onError) {
                    callbacks.onError(error);
                }
                dispatch({ type: COMPLETE });
            },
            newPasswordRequired: () => {
                if (skipForcingPasswordChange !== true) {
                    dispatch({ type: IDENTITY_SET_FORCE_CHANGE_PASSWORD, data: user });
                }
                if (callbacks.onNewPasswordNeeded) {
                    callbacks.onNewPasswordNeeded(user);
                }
                dispatch({ type: COMPLETE });
            }
        });
    };
}

/**
 * Logs out currently logged user.
 */
export function logout(onSuccess = () => null) {
    return async function(dispatch) {
        const user = userPool.getCurrentUser();
        if (user !== null) {
            user.signOut();
        }

        if (AWS.config.credentials) {
            AWS.config.credentials.clearCachedId();
            AWS.config.credentials = new AWS.CognitoIdentityCredentials({});
        }

        DataStorage.clear();
        dispatch({ type: IDENTITY_LOGOUT_OK });
        if (onSuccess && typeof onSuccess === 'function') {
            onSuccess();
        }
    };
}

/**
 * Handles Cognito call to make mandatory password change.
 * @param {Object} values
 */
export function changePassword(values, onSuccess = () => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });

        const { session, password } = values;
        session.completeNewPasswordChallenge(password, null, {
            onSuccess: session => {
                dispatch({ type: IDENTITY_LOGIN_OK, data: session });
                dispatch({ type: IDENTITY_UNSET_FORCE_CHANGE_PASSWORD });
                onSuccess(session);
                dispatch({ type: COMPLETE });
                Snackbar.success(<FormattedMessage id={'passwordUpdated'} defaultMessage={'Your password has been updated.'} />);
            },
            onFailure: error => {
                Snackbar.error(error.message);
                dispatch({ type: IDENTITY_UNSET_FORCE_CHANGE_PASSWORD });
                dispatch({ type: COMPLETE });
            }
        });
    };
}

export function forgotPassword(values, verify = () => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });

        const user = new CognitoUser({ Username: values.email.toLowerCase(), Pool: userPool });
        user.forgotPassword({
            onFailure: error => {
                let message = <FormattedMessage id={'intro.forgotPassword'} defaultMessage={'Unable to validate your email address.'} />;
                Snackbar.error(error && error.message ? error.message : message);
                dispatch({ type: IDENTITY_RESET_PASSWORD_COMPLETE });
                dispatch({ type: COMPLETE });
            },
            inputVerificationCode: () => {
                Snackbar.success(<FormattedMessage id={'intro.forgotPasswordStep2'} defaultMessage={'Please check your email for further instructions.'} />);
                dispatch({ type: IDENTITY_FORGOT_PASSWORD, data: user });
                dispatch({ type: COMPLETE });

                verify();
            }
        });
    };
}

export function resetPassword(values, onSuccess = () => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });

        const { session, code, newPassword } = values;
        session.confirmPassword(code, newPassword, {
            onSuccess: () => {
                Snackbar.success(
                    <FormattedMessage id={'intro.resetPassword'} defaultMessage={'Successfully changed password. Please use new password to log in.'} />
                );
                dispatch({ type: IDENTITY_RESET_PASSWORD_COMPLETE });
                dispatch({ type: COMPLETE });
                onSuccess();
            },
            onFailure: error => {
                Snackbar.error(error.message);
                dispatch({ type: IDENTITY_RESET_PASSWORD_COMPLETE });
                dispatch({ type: COMPLETE });
            }
        });
    };
}

/**
 * Retrieve profile data, company and role.
 * @param {Function} onSuccess
 * @param {Function} onError
 */
export function getProfile(onSuccess = response => null, onError = error => undefined) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: '/profile' })
            .then(response => {
                dispatch({ type: IDENTITY_GET_ROLES, data: response });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(<FormattedMessage id={'error.profileDetails'} defaultMessage={'Unable to retrieve profile details.'} />);
                dispatch({ type: COMPLETE });
                onError(error);
            });
    };
}

/**
 * Persists selected role in redux and local storage.
 * @param {Object} role
 * @param {Function} onComplete
 */
export function selectRole(role, onComplete = () => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        DataStorage.setItem('role', JSON.stringify(role));
        dispatch({ type: IDENTITY_SELECT_ROLE, data: role });
        dispatch({ type: COMPLETE });
        onComplete();
    };
}

/**
 * Retrieves user details for selected role.
 * @param {Object} role
 */
export function getUserDetails(role, onComplete = details => null) {
    let errorMessage = <FormattedMessage id={'error.userDetails'} defaultMessage={'Unable to retrieve user details.'} />;

    return async function(dispatch) {
        dispatch({ type: LOADING });
        const { id, type } = role;
        if (!id || !type) {
            Snackbar.error(errorMessage);
            dispatch({ type: COMPLETE });
            return;
        }

        call({ path: `/${type}/${id}` })
            .then(response => {
                dispatch({ type: IDENTITY_GET_USER_DETAILS, data: response });
                dispatch({ type: COMPLETE });
                onComplete(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function updateUserDetails(role, details, onComplete = details => null) {
    let errorMessage = <FormattedMessage id={'error.userDetailsUpdate'} defaultMessage={'Unable to update user details.'} />;

    return async function(dispatch) {
        dispatch({ type: SAVING });
        const { id, type } = role;
        call({ path: `/${type}/${id}`, method: 'PUT', body: details })
            .then(response => {
                dispatch({ type: IDENTITY_UPDATE_USER_DETAILS, data: details });
                dispatch({ type: COMPLETE });
                onComplete(details);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function getUserProfile(userId, onComplete = user => null) {
    let errorMessage = <FormattedMessage id={'error.userProfile'} defaultMessage={'Unable to retrieve user profile.'} />;
    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/user/${userId}` })
            .then(response => {
                dispatch({ type: IDENTITY_GET_USER_PROFILE, data: response });
                dispatch({ type: COMPLETE });
                if (response && response.status === 'pending') {
                    call({ path: `/user/${userId}/action/accept`, method: 'POST' });
                }
                onComplete(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function acceptUser(userId, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.acceptUser'} defaultMessage={'Unable to accept user.'} />;

    return async function(dispatch) {
        call({ path: `/user/${userId}/action/accept`, method: 'POST' })
            .then(response => {
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function updateUserProfile(data, onComplete = user => null) {
    let errorMessage = <FormattedMessage id={'error.userProfile'} defaultMessage={'Unable to update user profile.'} />;
    return async function(dispatch) {
        dispatch({ type: SAVING });
        call({ path: `/user/${data.id}`, method: 'PUT', body: data })
            .then(response => {
                dispatch({ type: IDENTITY_GET_USER_PROFILE, data: data });
                dispatch({ type: COMPLETE });
                onComplete(data);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

/**
 * Submits users terms agreement.
 * @param {Object} user
 * @param {Object} acceptingRole
 * @param {Function} onSuccess
 */
export function submitTermsAgreement(user, acceptingRole, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.submitTermsAgreement'} defaultMessage={'Unable to submit terms agreement.'} />;
    const { id } = user;
    const { role, resource, resource_id } = acceptingRole;

    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/user/${id}/role/${role}/${resource}/${resource_id}/action/accept`, method: 'POST' })
            .then(response => {
                let updatedRole = { ...acceptingRole, status: 'active' };
                dispatch({ type: IDENTITY_UPDATE_ROLE_STATUS, data: updatedRole });
                dispatch({ type: COMPLETE });
                onSuccess(updatedRole);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
        dispatch({ type: COMPLETE });
    };
}

/**
 * Submits users terms agreement.
 */
export function submitOnboarding(data, role, onSuccess = () => null) {
    let errorMessage = <FormattedMessage id={'error.submitOnboarding'} defaultMessage={'Unable to submit onboarding details.'} />;
    const { id } = data;
    const { type } = role;
    let action = 'submit';
    if (type === 'contractor') {
        action = 'approve';
    }

    return async function(dispatch) {
        dispatch({ type: LOADING });
        call({ path: `/${type}/${id}/action/${action}`, method: 'POST' })
            .then(response => {
                dispatch({ type: IDENTITY_GET_USER_DETAILS, data: { ...data, status: response.status } });
                dispatch({ type: COMPLETE });
                onSuccess(response);
            })
            .catch(error => {
                Snackbar.error(errorMessage);
                dispatch({ type: COMPLETE });
            });
    };
}

export function setImpersonateRole(targetUser, onComplete = () => null) {
    const { roles } = targetUser;
    let userRole = roles && roles.length > 0 ? roles[0] : undefined;
    if (!userRole) {
        Snackbar.error(<FormattedMessage id={'error.setImpersonateRole'} defaultMessage={'Unable to impersonate user.'} />);
        return;
    }

    const role = userRole.id !== undefined ? userRole : { ...userRole, id: userRole.resource_id, type: userRole.resource };

    return async function(dispatch) {
        dispatch({ type: LOADING });
        dispatch({ type: IDENTITY_SET_IMPERSONATE_ROLE, data: { ...targetUser, role, roles: undefined } });
        dispatch({ type: COMPLETE });
        onComplete(role);
    };
}

export function unsetImpersonateRole(onComplete = () => null) {
    return async function(dispatch) {
        dispatch({ type: LOADING });
        dispatch({ type: IDENTITY_SET_IMPERSONATE_ROLE, data: undefined });
        dispatch({ type: COMPLETE });
        onComplete();
    };
}



// WEBPACK FOOTER //
// ./src/reducers/identity.jsx